import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { trim as _trim } from 'lodash';
import { currentUsername } from './current-username';
import { ReturnsApiService } from './returns-api.service';

// What became of each item in a return package once it was opened at the
// shop: put back on the shelf (and keyed into lnwshop stock by hand), or
// written off because it cannot be sold again. One document per item line
// in its own collection (management-console.return_inspections, through the
// returns Lambda), next to the receipt confirmations.
//
// The stock itself is still keyed in lnwstore — this only records that it
// was done, and by whom, so the report can show what is still waiting.

export interface InspectionMark {
    // Units put back into sellable stock.
    restocked: number;
    // Units disposed of — damaged, used, missing parts.
    writtenOff: number;
    // Why, when it matters (the write-off reason, a partial count, ...).
    note?: string;
    // ISO datetime of the last edit, and who made it when known.
    at: string;
    by?: string;
}

// Keyed by `<ReturnPackage.id>#<sku>`: one mark per item line of a return.
export interface ReturnInspectionsDoc {
    items: Record<string, InspectionMark>;
}

export const inspectionKey = (returnRowId: string, sku: string): string =>
    `${returnRowId}#${_trim(sku)}`;

// Where an item stands against the quantity that came back.
//   pending  nothing recorded yet
//   partial  some units recorded, fewer than came back
//   done     every unit accounted for (restocked + written off >= qty)
export type InspectionStatus = 'pending' | 'partial' | 'done';

export const INSPECTION_STATUS_LABELS: Record<InspectionStatus, string> = {
    pending: 'รอตรวจสอบ',
    partial: 'ตรวจบางส่วน',
    done: 'ตรวจแล้ว',
};

export const inspectionStatus = (
    mark: InspectionMark | null | undefined,
    qty: number,
): InspectionStatus => {
    if (!mark) {
        return 'pending';
    }
    const handled = (mark.restocked || 0) + (mark.writtenOff || 0);
    if (handled <= 0) {
        return 'pending';
    }
    return handled >= qty ? 'done' : 'partial';
};

@Injectable({
    providedIn: 'root',
})
export class ReturnInspectionService {
    private api = inject(ReturnsApiService);

    // Every mark on record; empty until the first.
    getDoc(): Observable<ReturnInspectionsDoc> {
        return this.api.request<ReturnInspectionsDoc>('inspections');
    }

    // Records (or replaces) the mark under `key`, stamped by the server with
    // now and here with the signed-in user; resolves to every mark on record.
    set(
        key: string,
        mark: Pick<InspectionMark, 'restocked' | 'writtenOff' | 'note'>,
    ): Observable<ReturnInspectionsDoc> {
        return currentUsername().pipe(
            switchMap((by) =>
                this.api.request<ReturnInspectionsDoc>('setInspection', {
                    key,
                    restocked: Math.max(0, Math.floor(mark.restocked || 0)),
                    writtenOff: Math.max(0, Math.floor(mark.writtenOff || 0)),
                    ...(mark.note?.trim() ? { note: mark.note.trim() } : {}),
                    ...(by ? { by } : {}),
                }),
            ),
        );
    }

    clear(key: string): Observable<ReturnInspectionsDoc> {
        return this.api.request<ReturnInspectionsDoc>('clearInspection', {
            key,
        });
    }
}
